import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { supabase } from '../supabaseClient';
import { resolveAuthSession } from '../lib/auth/storedSession';
import {
  buildLoginRedirectPath,
  isLaunchUrlAlreadyHandled,
  markLaunchUrlHandled,
  resolveDeepLinkPath,
  urlToAppPath,
} from '../lib/deepLink';

/**
 * Native deep link routing (prii:// now, https://prii.app/… via Universal Links later).
 * Must render inside <BrowserRouter>.
 */

// Dedupe concurrent getLaunchUrl() reads within a single page load.
let launchUrlFetchPromise = null;

function logNativeAppPluginMissing(err) {
  if (err?.code !== 'UNIMPLEMENTED') return false;
  console.error(
    '[DeepLink] @capacitor/app native plugin is not registered (UNIMPLEMENTED). ' +
      'Deep links will not work. Run npm run build:ios, then in Xcode: ' +
      'File → Packages → Reset Package Caches, clean build (Shift+Cmd+K), and rebuild.',
  );
  return true;
}

function getLaunchUrlOnce() {
  if (!launchUrlFetchPromise) {
    launchUrlFetchPromise = App.getLaunchUrl().catch((err) => {
      if (!logNativeAppPluginMissing(err)) {
        console.warn('[DeepLink] getLaunchUrl failed', err);
      }
      return null;
    });
  }
  return launchUrlFetchPromise;
}

export default function DeepLinkHandler() {
  // navigate changes with every page; read through a ref so the native link listener is added once, not re-added on
  // every tap (2026-10-30 speed audit)
  const navigateNow = useNavigate();
  const navRef = useRef(navigateNow);
  navRef.current = navigateNow;
  const recentUrlsRef = useRef(new Map());

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    const shouldProcess = (rawUrl) => {
      const now = Date.now();
      const last = recentUrlsRef.current.get(rawUrl);
      if (last != null && now - last < 3000) return false;
      recentUrlsRef.current.set(rawUrl, now);
      return true;
    };

    const routeIncomingUrl = async (rawUrl, { commitLaunchUrl } = {}) => {
      if (!shouldProcess(rawUrl)) return;

      const appPath = urlToAppPath(rawUrl);
      const routePath = resolveDeepLinkPath(appPath);
      if (!routePath) {
        console.warn('[DeepLink] unrecognized or out-of-scope path', { rawUrl, appPath });
        commitLaunchUrl?.();
        navRef.current('/', { replace: true });
        return;
      }

      const {
        data: { session: liveSession },
      } = await supabase.auth.getSession();
      // Offline, an expired token makes getSession() answer null; a stored session is still signed in.
      const session = resolveAuthSession(liveSession);
      if (commitLaunchUrl?.cancelled()) return;

      commitLaunchUrl?.();
      if (!session?.user?.id) {
        // An invite link shows its group first (the signed-out join card says what you are joining, then takes
        // you to sign up / log in and joins you when you are back - Sam, 2026-09-28). Everything else: login.
        if (/^\/groups\/[^/?]+.*[?&]invite=/.test(routePath)) {
          navRef.current(routePath, { replace: true });
          return;
        }
        navRef.current(buildLoginRedirectPath(routePath), { replace: true });
        return;
      }

      navRef.current(routePath);
    };

    let cancelled = false;

    getLaunchUrlOnce().then((launch) => {
      if (cancelled || !launch?.url) return;
      if (isLaunchUrlAlreadyHandled(launch.url)) return;

      const commitLaunchUrl = () => {
        if (cancelled) return;
        markLaunchUrlHandled(launch.url);
      };
      commitLaunchUrl.cancelled = () => cancelled;

      return routeIncomingUrl(launch.url, { commitLaunchUrl });
    });

    const listenerPromise = App.addListener('appUrlOpen', (event) => {
      if (event?.url) routeIncomingUrl(event.url);
    });

    return () => {
      cancelled = true;
      listenerPromise.then((handle) => handle.remove()).catch(() => {});
    };
  }, []);

  return null;
}
